/**
 * -----------------------------------------------------------------------------
 * DISCLAIMER
 * -----------------------------------------------------------------------------
 * This file is part of the Breeze client application.
 * The functions, types, and controls defined here are intended for internal use
 * within the project and may not cover every edge case. They are provided
 * "AS IS" without warranty of any kind, express or implied, including but not
 * limited to correctness, completeness, or fitness for a particular purpose.
 *
 * By using these functions you accept that:
 * - They may change or be removed without notice.
 * - You are responsible for verifying correctness for your specific use case.
 * - The maintainers are not liable for any damage or issues resulting from use.
 *
 * When reusing or extending these functions:
 * - Ensure inputs are validated.
 * - Wrap in appropriate error handling.
 * - Keep performance considerations in mind (especially on large datasets).
 * -----------------------------------------------------------------------------
 */
import * as L from "leaflet";
import { ApiPortEntry, PortEntry, StationEntry } from "../../domain/visuals/SearchControl";
import { markerToStation } from "../markers/createMarker";
import { getStationById } from "../../domain/storage/stationsStore";

/** Extracts a searchable label and popup text from a marker */
export function buildStationsFromMarkers(markers: L.Marker[]): StationEntry[] {
	return markers.map((m, i) => {
		let extraParameters = "";
		const station = getStationById(markerToStation.get(m));
		if (station !== undefined) {
			extraParameters = station.Description + " | " + station.Owner;
			extraParameters = station.Id;
		}
		// If you store name in marker options or a custom property:
		const name = (m as any).options?.title || (m as any).name || `Pin ${i + 1}`;
		return {
			id: (m as any)._leaflet_id?.toString?.() ?? `pin-${i}`,
			marker: m,
			name,
			content: extraParameters,
			owner: station?.Owner ?? ""
		};
	});
}

/** Create named areas users can search and fitBounds to */
export function createPorts(defs: Array<ApiPortEntry>): PortEntry[] {
	return defs.map(d => (createPort(d)));
}
export function createPort(d: ApiPortEntry): PortEntry {
	return {
		id: d.Id,
		name: d.Name,
		ne: { lat: d.NorthEast.Latitude, lng: d.NorthEast.Longitude },
		sw: { lat: d.SouthWest.Latitude, lng: d.SouthWest.Longitude }
	};
}
